// Infer reading direction from text baselines, never from paper width/height.
// Numeric dimensions are deliberately ignored: the title block wins over quotes.
const pageRotations = new WeakMap();
const rasterPages = new WeakSet();
const scanRequests = new Map();
const titleBlock = /\b(scala|scale|disegn\w*|drawing|material\w*|revision\w*|rev|sheet|foglio|denominazione|description|descrizione|approved|approv\w*|date|data|codice|code|weight|peso|toleran\w*|cliente|customer)\b/i;
const normalize = (angle) => ((angle % 360) + 360) % 360;

export function inferPdfRotation(items, sourceRotation = 0) {
    const votes = [0, 0, 0, 0], titles = [0, 0, 0, 0];
    for (const item of items || []) {
        const text = String(item.str || "").trim();
        const letters = (text.match(/[a-zÀ-ž]/gi) || []).length;
        if (letters < 3 || !item.transform) continue;
        const [a, b] = item.transform;
        if (!Number.isFinite(a) || !Number.isFinite(b) || Math.hypot(a, b) < 0.01) continue;
        const angle = normalize(Math.atan2(b, a) * 180 / Math.PI);
        const quadrant = Math.round(angle / 90) % 4;
        if (Math.min(normalize(angle - quadrant * 90), normalize(quadrant * 90 - angle)) > 8) continue;
        votes[quadrant] += Math.min(letters, 32);
        if (titleBlock.test(text)) titles[quadrant] += Math.min(letters, 32);
    }
    const titleTotal = titles.reduce((a, b) => a + b, 0);
    const weights = titleTotal >= 12 ? titles : votes;
    const total = weights.reduce((a, b) => a + b, 0);
    const winner = weights.indexOf(Math.max(...weights));
    // Ambiguous or scanned sheets keep the PDF's supplied orientation.
    if (total < 12 || weights[winner] / total < 0.7) return 0;
    return normalize(winner * 90 - sourceRotation);
}

export function readPdfPageRotation(page) {
    if (!pageRotations.has(page)) {
        pageRotations.set(page, page.getTextContent().then(({ items }) => {
            const letters = items.reduce((total, item) => total + Math.min((String(item.str || "").match(/[a-zÀ-ž]/gi) || []).length, 32), 0);
            if (letters < 12) rasterPages.add(page);
            return inferPdfRotation(items, page.rotate);
        }).catch(() => 0));
    }
    return pageRotations.get(page);
}

export async function readScanOrientation(pdf, identity, work, number = 1) {
    if (!identity?.source_sha256 || !identity.article || !identity.drawing) return null;
    const page = await pdf.getPage(number);
    await readPdfPageRotation(page);
    const key = `${identity.revision_id || 'original'}:${identity.source_sha256}:${number}`;
    if (!scanRequests.has(key) && !rasterPages.has(page)) return null;
    if (!scanRequests.has(key)) {
        if (scanRequests.size >= 64) scanRequests.delete(scanRequests.keys().next().value);
        const request = work.post("/pdf/orientation/", { article: identity.article, drawing: identity.drawing,
            source_sha256: identity.source_sha256, page: number, ...(identity.revision_id ? { revision_id: identity.revision_id } : {}) }, true).then(({ data, status }) => {
            if (status === 202 || data.rotation === null) { scanRequests.delete(key); return null; }
            return [0, 90, 180, 270].includes(data.rotation) ? data.rotation : null;
        }).catch(() => { scanRequests.delete(key); return null; });
        scanRequests.set(key, request);
    }
    const rotation = await scanRequests.get(key);
    if (rotation !== null && rotation !== undefined) pageRotations.set(page, Promise.resolve(rotation));
    return rotation;
}
